import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/Common";
import { fmtMoney, fmtDateTime, fmtDuration } from "@/lib/format";
import {
  ArrowLeft, FilePdf, CheckCircle, PencilSimple, Copy, Warning, ClockCounterClockwise,
  MapPin, GitBranch, Prohibit, Archive, Path,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [overrideReason, setOverrideReason] = useState("");
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/quotes/${id}`);
    setQuote(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!quote) return null;
  const c = quote.costing || {};
  const readOnly = quote.read_only || !quote.is_latest;

  const openPdf = async () => {
    try {
      const res = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open PDF"); }
  };

  const doAccept = async (withOverride) => {
    try {
      const body = withOverride ? { conflict_override_reason: overrideReason } : {};
      const { data } = await api.post(`/quotes/${id}/accept`, body);
      toast.success("Quote accepted — booking created");
      if (data.soft_conflicts?.length) toast.warning(`Turnaround warning: ${data.soft_conflicts.length} nearby booking(s)`);
      setAcceptOpen(false); setConflicts([]); setOverrideReason(""); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.conflicts) {
        setConflicts(detail.conflicts); setAcceptOpen(true);
      } else toast.error(formatApiError(detail));
    }
  };

  const changeStatus = async () => {
    try { await api.post(`/quotes/${id}/status`, { status: newStatus, reason: statusReason }); toast.success("Status updated"); setStatusOpen(false); setStatusReason(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const archiveQuote = async () => {
    if (!window.confirm("Archive this quote? It stays in history but is filtered out of active views.")) return;
    try { await api.post(`/quotes/${id}/status`, { status: "archived" }); toast.success("Quote archived"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicate = async () => { const { data } = await api.post(`/quotes/${id}/duplicate`); toast.success("Duplicated"); navigate(`/quotes/${data.id}`); };
  const recalc = async () => { await api.post(`/quotes/${id}/recalc`); toast.success("Recalculated to current cost profile"); load(); };

  const warnBox = c.warning === "red"
    ? "bg-red-50 border-l-4 border-red-500 text-red-800"
    : c.warning === "amber" ? "bg-amber-50 border-l-4 border-amber-500 text-amber-800" : null;

  return (
    <div>
      <button onClick={() => navigate("/quotes")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> Back to quotes
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900 font-mono">{quote.quote_number}</h1>
            <span className="text-slate-400 font-mono">v{quote.version}</span>
            <StatusBadge status={quote.status} />
            {readOnly && <span className="text-xs bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full">read-only revision</span>}
          </div>
          <p className="text-sm text-slate-500 mt-1">{quote.client_snapshot?.name || "No client"} · {quote.vehicle_snapshot?.name} · created {fmtDateTime(quote.created_at)}{quote.created_by_name ? ` · by ${quote.created_by_name}` : ""}</p>
          {(quote.title || quote.load_reference) && (
            <p className="text-sm text-slate-600 mt-0.5">
              {quote.title && <span className="font-medium">{quote.title}</span>}
              {quote.title && quote.load_reference && <span className="text-slate-300"> · </span>}
              {quote.load_reference && <span data-testid="detail-load-ref">Load ref: <span className="font-mono">{quote.load_reference}</span></span>}
            </p>
          )}
          {quote.quote_mode === "external" && (
            <p className="text-sm text-slate-600 mt-0.5" data-testid="detail-supplier">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 mr-2">3rd party</span>
              Supplier: <span className="font-medium">{quote.supplier_snapshot?.name || "—"}</span> · Hire cost {fmtMoney(quote.hire_cost)} <span className="text-slate-400">(internal only — not on client PDF)</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={openPdf} data-testid="pdf-btn"><FilePdf size={16} className="mr-1.5" />PDF</Button>
          <Button variant="outline" onClick={duplicate} data-testid="duplicate-btn"><Copy size={16} className="mr-1.5" />Duplicate</Button>
          {quote.status === "draft" && !readOnly && (
            <Button variant="outline" onClick={() => navigate(`/quote/${id}`)} data-testid="edit-btn"><PencilSimple size={16} className="mr-1.5" />Edit</Button>
          )}
          {quote.status === "draft" && (
            <Button onClick={() => doAccept(false)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="accept-btn"><CheckCircle size={16} weight="fill" className="mr-1.5" />Accept</Button>
          )}
          {(quote.status === "accepted" || quote.status === "completed") && !readOnly && (
            <Button onClick={() => navigate(`/quote/${id}?amend=1`)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="amend-btn"><GitBranch size={16} className="mr-1.5" />Amend → v{quote.version + 1}</Button>
          )}
          {(quote.status === "accepted" || quote.status === "completed" || quote.status === "declined") && quote.status !== "archived" && (
            <Button variant="outline" onClick={archiveQuote} data-testid="archive-btn" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-200"><Archive size={16} className="mr-1.5" />Archive</Button>
          )}
          {!readOnly && (
            <Button variant="outline" onClick={() => { setNewStatus(quote.status); setStatusOpen(true); }} data-testid="status-btn"><Archive size={16} className="mr-1.5" />Status</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Route */}
          <Card title="Route" icon={Path}>
            <div className="space-y-0">
              {quote.stages?.map((s, i) => (
                <div key={i} className="flex gap-3" data-testid={`detail-stage-${i}`}>
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{i + 1}</div>
                    {i < quote.stages.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className="pb-4 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-slate-400">{s.type}</span>
                      {s.lat && <MapPin size={12} className="text-green-600" weight="fill" />}
                    </div>
                    <div className="font-medium text-slate-900">{s.name || s.address || "Unnamed stop"}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      Arr {fmtDateTime(s.arrival)} · Dep {fmtDateTime(s.departure)}
                      {i < (quote.legs?.length || 0) && ` · leg ${quote.legs[i].distance_km} km / ${fmtDuration(quote.legs[i].drive_minutes)}${quote.legs[i].loaded ? " (loaded)" : " (empty)"}`}
                    </div>
                    {i < (quote.legs?.length || 0) && quote.legs[i].override_reason && (
                      <div className="text-xs text-amber-700 mt-0.5">override: {quote.legs[i].override_reason}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-1 mt-2 pt-3 border-t border-slate-100 text-sm">
              <Meta label="Total distance" value={`${c.total_km} km`} />
              <Meta label="Trip start" value={fmtDateTime(quote.trip_start)} />
              <Meta label="Trip end" value={fmtDateTime(quote.trip_end)} />
              <Meta label="Span" value={`${quote.span_days} day(s)`} />
              {quote.route_calculated_at && <Meta label="Route calc'd" value={fmtDateTime(quote.route_calculated_at)} />}
            </div>
          </Card>

          {/* Revisions */}
          <Card title="Revision history" icon={GitBranch}>
            <div className="space-y-2">
              {quote.revisions?.map((r) => (
                <button key={r.id} onClick={() => navigate(`/quotes/${r.id}`)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md border text-left transition-colors ${r.id === quote.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`revision-${r.version}`}>
                  <div>
                    <span className="font-mono font-semibold text-sm">v{r.version}</span>
                    <span className="text-xs text-slate-500 ml-2">{r.version_reason || "initial version"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="data-num text-sm">{fmtMoney(r.costing?.selling_ex_vat)}</span>
                    <span className="text-xs text-slate-400">{fmtDateTime(r.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Audit */}
          <Card title="Audit trail" icon={ClockCounterClockwise}>
            <div className="space-y-1.5 max-h-72 overflow-y-auto cd-scroll">
              {quote.audit?.map((a) => (
                <div key={a.id} className="flex items-start gap-2 text-sm" data-testid={`audit-${a.id}`}>
                  <span className="text-xs text-slate-400 font-mono whitespace-nowrap w-32 shrink-0">{fmtDateTime(a.timestamp)}</span>
                  <span className="font-medium text-slate-700">{a.action.replace(/_/g, " ")}</span>
                  <span className="text-xs text-slate-400">{a.actor_email}</span>
                  {a.detail?.reason && <span className="text-xs text-amber-700 italic">"{a.detail.reason}"</span>}
                </div>
              ))}
              {(!quote.audit || quote.audit.length === 0) && <p className="text-sm text-slate-400">No audit events.</p>}
            </div>
          </Card>
        </div>

        {/* Costing panel */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md sticky top-20">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Costing</h3>
              {!readOnly && <button onClick={recalc} className="text-xs text-slate-500 hover:text-slate-900" data-testid="recalc-btn">Recalc to current</button>}
            </div>
            <div className="p-5">
              {warnBox && (
                <div className={`${warnBox} p-3 rounded-r-md text-sm mb-4 flex items-start gap-2`} data-testid="cost-warning">
                  <Warning size={18} weight="fill" className="mt-0.5 shrink-0" />
                  <div>{c.warning === "red" ? "Below variable cost floor" : "Below break-even"}. {quote.below_cost_reason && <span className="italic">Reason: {quote.below_cost_reason}</span>}</div>
                </div>
              )}
              {quote.quote_mode === "external"
                ? <Row label={`3rd party hire${quote.supplier_snapshot?.name ? ` — ${quote.supplier_snapshot.name}` : ""}`} value={fmtMoney(c.hire_cost)} />
                : (<>
                  <Row label="Variable / km" value={fmtMoney(c.variable_km_cost)} />
                  <Row label="Fuel" value={fmtMoney(c.fuel_cost)} />
                </>)}
              <Row label="Tolls" value={fmtMoney(c.tolls_total)} />
              {Number(c.overtime_amount) > 0 && <Row label={`Driver overtime${quote.overtime_hours ? ` (${quote.overtime_hours} hrs @ ${fmtMoney(quote.overtime_rate)})` : ""}`} value={fmtMoney(c.overtime_amount)} muted />}
              {Number(c.standing_amount) > 0 && <Row label={`Standing time${quote.standing_hours ? ` (${quote.standing_hours} hrs)` : ""}`} value={fmtMoney(c.standing_amount)} muted />}
              {Number(c.assistant_amount) > 0 && <Row label={`Assistants${quote.assistant_qty ? ` (${quote.assistant_qty})` : ""}`} value={fmtMoney(c.assistant_amount)} muted />}
              {Number(c.tail_lift_amount) > 0 && <Row label="Tail lift" value={fmtMoney(c.tail_lift_amount)} muted />}
              {Number(c.additional_amount) > 0 && <Row label={quote.additional_charge_desc ? `Additional — ${quote.additional_charge_desc}` : "Additional charge"} value={fmtMoney(c.additional_amount)} muted />}
              <Row label="Variable floor" value={fmtMoney(c.variable_floor)} bold />
              {quote.quote_mode !== "external" && (<>
              <div className="my-2 border-t border-dashed border-slate-200" />
              <Row label={`Fixed (${c.fixed_units_display} units)`} value={fmtMoney(c.fixed_total)} />
              <Row label={`Daily unit × ${c.fixed_units_display} @ ${c.fixed_recovery_pct}% recovery`} value={fmtMoney(c.daily_fixed_unit)} muted />
              <Row label="Break-even" value={fmtMoney(c.break_even)} bold />
              </>)}
              <div className="my-2 border-t border-slate-200" />
              <Row label="Quoted cost base" value={fmtMoney(c.quoted_cost_base)} />
              <Row label={`Margin (${c.margin_pct}%)`} value={fmtMoney(c.profit)} />
              <div className="mt-3 pt-3 border-t-2 border-slate-900 flex items-center justify-between">
                <span className="font-heading font-bold text-slate-900">Selling (excl VAT)</span>
                <span className="data-num font-bold text-xl text-slate-900" data-testid="detail-total">{fmtMoney(c.selling_ex_vat)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accept conflict dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2 text-red-700"><Prohibit size={20} weight="fill" />Booking conflict</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This trip overlaps an existing booking on {quote.vehicle_snapshot?.name}:</p>
          <div className="space-y-2">
            {conflicts.map((cf) => (
              <div key={cf.id} className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                <span className="font-mono font-semibold">{cf.quote_number}</span> · {fmtDateTime(cf.start)} → {fmtDateTime(cf.end)}
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Override reason (required)</label>
            <textarea data-testid="override-reason" className="cd-input mt-1" rows={2} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={!overrideReason.trim()} onClick={() => doAccept(true)} data-testid="confirm-override">Override & accept</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Change status</DialogTitle></DialogHeader>
          <select data-testid="status-select" className="cd-input capitalize" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
            {["draft", "accepted", "completed", "declined", "archived", "deleted"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reason (optional)</label>
            <textarea className="cd-input mt-1" rows={2} value={statusReason} onChange={(e) => setStatusReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={changeStatus} data-testid="confirm-status">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        {Icon && <Icon size={18} className="text-slate-400" weight="bold" />}
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Row({ label, value, bold, muted }) {
  if (muted) {
    return <div className="flex items-center justify-between py-0.5 pl-3"><span className="text-xs text-slate-400">{label}</span><span className="data-num text-xs text-slate-400">{value}</span></div>;
  }
  return <div className="flex items-center justify-between py-1"><span className={`text-sm ${bold ? "font-semibold text-slate-900" : "text-slate-500"}`}>{label}</span><span className={`data-num text-sm ${bold ? "font-semibold text-slate-900" : "text-slate-700"}`}>{value}</span></div>;
}
function Meta({ label, value }) { return <div><div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div><div className="data-num text-slate-800">{value}</div></div>; }
